import React, { useState } from "react";
import { Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { distMi, bearingDeg } from "../../lib/followedFires";

const DIRS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const compass = (b) => DIRS[Math.round((((b % 360) + 360) % 360) / 45) % 8];

// Nearest fire to a strike (within 30 mi) — computed only for pinned strikes.
function nearestFire(s, fires) {
  let best = null;
  for (const f of fires) {
    if (f.latitude == null || !f.name) continue;
    const d = distMi(s.latitude, s.longitude, f.latitude, f.longitude);
    if (d <= 30 && (!best || d < best.d)) {
      best = { d, name: f.name, dir: compass(bearingDeg(f.latitude, f.longitude, s.latitude, s.longitude)) };
    }
  }
  return best;
}

// Age buckets: fresh strikes burst in big with a red ring, then shrink/dim as they age
function ageClass(ageS) {
  if (ageS < 600) return "ltg-fresh";
  if (ageS < 1800) return "ltg-mid";
  if (ageS < 3600) return "ltg-old";
  if (ageS < 86400) return "ltg-day";
  return "ltg-stale";
}

const _iconCache = {};
function strikeIcon(cls) {
  if (!_iconCache[cls]) {
    _iconCache[cls] = L.divIcon({
      className: "ltg-marker",
      html: `<span class="ltg-dot ${cls}"></span>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
  }
  return _iconCache[cls];
}

function ageLabel(ageS) {
  const m = Math.round(ageS / 60);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(ageS / 3600);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function strikeTime(s) {
  const d = new Date(s.at * 1000);
  const opts = { hour: "numeric", minute: "2-digit" };
  if (Date.now() - d.getTime() > 86400e3) {
    return d.toLocaleString(undefined, { month: "short", day: "numeric", ...opts });
  }
  return d.toLocaleTimeString(undefined, opts);
}

export default function LightningLayer({ strikes = [], fires = [] }) {
  // Click a strike to pin its label open (several can stay open for screenshots); click again or tap ✕ to close
  const [open, setOpen] = useState(() => new Set());
  const toggle = (k) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return strikes.map((s) => {
    const k = `${s.at}-${s.latitude}-${s.longitude}`;
    const isOpen = open.has(k);
    const nf = isOpen ? nearestFire(s, fires) : null;
    return (
      <Marker
        key={`ltg-${k}-${isOpen ? "o" : "c"}`}
        position={[s.latitude, s.longitude]}
        icon={strikeIcon(ageClass(s.age_s))}
        zIndexOffset={600}
        eventHandlers={{ click: () => toggle(k) }}
      >
        <Tooltip
          permanent={isOpen}
          interactive={isOpen}
          direction="top"
          offset={[0, -6]}
          eventHandlers={isOpen ? { click: () => toggle(k) } : undefined}
        >
          ⚡ {strikeTime(s)} · {ageLabel(s.age_s)}
          {isOpen ? "  ✕" : ""}
          {nf && (
            <>
              <br />
              🔥 {nf.d.toFixed(2)} mi {nf.dir} of {nf.name}
            </>
          )}
        </Tooltip>
      </Marker>
    );
  });
}
